import { debounce } from '@theme/utilities';

/**
 * Solios: keep the main product title (the <h1> in the details panel) on ONE
 * line, sized to fill the panel width — but never larger than the theme's
 * default size (that's the max). Long titles shrink to fit; titles that already
 * fit stay at the max. CSS can't size text to a container based on the text's
 * length, so we measure and set the font-size here.
 *
 * Runs on load, after web fonts load, on resize, and if the title text changes
 * (combined-listing product switch). Desktop only (>=750px).
 */
function fitTitle() {
  const h1 = document.querySelector('.product-details h1');
  if (!h1) return;

  const panel = h1.closest('.product-details');
  if (!panel) return;

  const block = h1.closest('.text-block') || h1.parentElement;

  // Mobile: clear any inline overrides and bail.
  if (window.innerWidth < 750) {
    for (const prop of ['font-size', 'white-space', 'line-height', 'padding-top']) {
      h1.style.removeProperty(prop);
    }
    block?.style.removeProperty('max-width');
    return;
  }

  // One line, full glyphs (line-height gives the ascenders room so the tops of
  // the letters aren't clipped), and let it exceed the block's default max-width.
  block?.style.setProperty('max-width', 'none', 'important');
  h1.style.whiteSpace = 'nowrap';
  h1.style.setProperty('line-height', '1.1', 'important');
  h1.style.setProperty('padding-top', '0.06em', 'important');

  // Available width = the panel's content box.
  const pcs = getComputedStyle(panel);
  const avail = panel.clientWidth - parseFloat(pcs.paddingLeft) - parseFloat(pcs.paddingRight);
  if (avail <= 0) return;

  // Max = the theme's default title size (measure with our override removed).
  h1.style.removeProperty('font-size');
  const maxSize = parseFloat(getComputedStyle(h1).fontSize) || 40;

  // Rendered width of the title text (one line) at the max size.
  const range = document.createRange();
  range.selectNodeContents(h1);
  const textW = range.getBoundingClientRect().width;
  if (textW <= 0) return;

  // Keep the max if it already fits; otherwise shrink just enough to fit one line.
  let target = maxSize;
  if (textW > avail) target = Math.max(24, maxSize * (avail / textW));

  h1.style.setProperty('font-size', `${target}px`, 'important');
}

const run = () => requestAnimationFrame(fitTitle);

run();
if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
window.addEventListener('resize', debounce(run, 100));

// Re-fit if the title text changes (e.g. a combined-listing product switch).
const h1 = document.querySelector('.product-details h1');
if (h1) {
  new MutationObserver(run).observe(h1, { childList: true, characterData: true, subtree: true });
}
